import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, X, Users, Map, Building2, ScrollText, NotebookPen,
  CalendarClock, CalendarDays, History, BookOpen,
  ArrowRight, Plus, Eye, Sparkles, Moon, Sun, Layout,
  Compass, Flame, BookMarked, FileText, Database,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import type { SearchResult } from '../hooks/useGlobalSearch';
import type { CollectionKey } from '../../shared/types';
import { useNavigation } from '../contexts/NavigationContext';

interface GlobalSearchProps {
  query: string;
  setQuery: (query: string) => void;
  results: SearchResult[];
  isOpen: boolean;
  onClose: () => void;
  onSelectResult: (category: CollectionKey, id: number) => void;
  tabs?: string[];
  activeTab?: string;
  onNavigateToTab?: (tab: string) => void;
  onOpenQuickAdd?: () => void;
  onOpenAssistant?: () => void;
  onToggleTheme?: () => void;
  theme?: 'light' | 'dark';
}

const categoryIcons: Record<CollectionKey, typeof Users> = {
  playerCharacters: Users,
  npcs: Users,
  locations: Map,
  organizations: Building2,
  plotThreads: ScrollText,
  sessionNotes: NotebookPen,
  calendars: CalendarDays,
  calendarEvents: CalendarClock,
  futureContent: CalendarClock,
  wikiArticles: BookOpen,
};

const categoryColors: Record<CollectionKey, string> = {
  playerCharacters: 'text-[var(--accent-characters)]',
  npcs: 'text-[var(--accent-npcs)]',
  locations: 'text-[var(--accent-locations)]',
  organizations: 'text-[var(--accent-organizations)]',
  plotThreads: 'text-[var(--accent-plots)]',
  sessionNotes: 'text-[var(--accent-sessions)]',
  calendars: 'text-[var(--info)]',
  calendarEvents: 'text-[var(--info)]',
  futureContent: 'text-[var(--accent-future)]',
  wikiArticles: 'text-[var(--text-secondary)]',
};

const TAB_ICONS: Record<string, typeof Users> = {
  overview: Eye,
  oracle: Compass,
  play: Layout,
  characters: Users,
  heat: Flame,
  npcs: Users,
  locations: Map,
  organizations: Building2,
  plots: ScrollText,
  sessions: NotebookPen,
  calendar: CalendarDays,
  wiki: BookOpen,
  future: CalendarClock,
  data: Database,
  maps: Map,
  rules: BookMarked,
  sheets: FileText,
};

const TAB_LABELS: Record<string, string> = {
  overview: 'Overview',
  oracle: 'Oracle',
  play: 'Session',
  characters: 'Characters',
  heat: 'Heat',
  npcs: 'NPCs',
  locations: 'Locations',
  organizations: 'Organizations',
  plots: 'Plots',
  sessions: 'Sessions',
  calendar: 'Calendar',
  wiki: 'Codex',
  future: 'Future',
  data: 'Archive',
  maps: 'Maps',
  timelines: 'Timelines',
  generators: 'Structures',
  whiteboards: 'Whiteboard',
  'content-trees': 'Content Trees',
  rules: 'Rules',
  sheets: 'Sheets',
  encounters: 'Encounters',
  publish: 'Publish',
  memory: 'Memory',
  integrations: 'Integrations',
};

interface CommandAction {
  id: string;
  label: string;
  description: string;
  icon: typeof Users;
  action: () => void;
  keywords: string[];
}

export default function GlobalSearch({
  query,
  setQuery,
  results,
  isOpen,
  onClose,
  onSelectResult,
  tabs,
  onNavigateToTab,
  onOpenQuickAdd,
  onOpenAssistant,
  onToggleTheme,
  theme,
}: GlobalSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const navigation = useNavigation();
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
    if (isOpen) {
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Build command actions
  const commandActions = useMemo<CommandAction[]>(() => {
    const actions: CommandAction[] = [];

    // Tab navigation actions
    if (tabs && onNavigateToTab) {
      for (const tab of tabs) {
        const label = TAB_LABELS[tab] || tab.charAt(0).toUpperCase() + tab.slice(1);
        actions.push({
          id: `nav-${tab}`,
          label: `Go to ${label}`,
          description: 'Navigate',
          icon: TAB_ICONS[tab] || ArrowRight,
          action: () => {
            onNavigateToTab(tab);
            onClose();
          },
          keywords: ['go', 'navigate', 'open', tab, label.toLowerCase()],
        });
      }
    }

    // Quick actions
    if (onOpenQuickAdd) {
      actions.push({
        id: 'quick-add',
        label: 'Quick Add',
        description: 'Create new item',
        icon: Plus,
        action: () => {
          onOpenQuickAdd();
          onClose();
        },
        keywords: ['add', 'create', 'new', 'quick'],
      });
    }

    if (onOpenAssistant) {
      actions.push({
        id: 'open-assistant',
        label: 'Open AI Assistant',
        description: 'Ask the assistant',
        icon: Sparkles,
        action: () => {
          onOpenAssistant();
          onClose();
        },
        keywords: ['ai', 'assistant', 'chat', 'help'],
      });
    }

    if (onToggleTheme) {
      actions.push({
        id: 'toggle-theme',
        label: theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode',
        description: 'Toggle theme',
        icon: theme === 'dark' ? Sun : Moon,
        action: () => {
          onToggleTheme();
          onClose();
        },
        keywords: ['theme', 'dark', 'light', 'mode', 'toggle'],
      });
    }

    return actions;
  }, [tabs, onNavigateToTab, onOpenQuickAdd, onOpenAssistant, onToggleTheme, theme, onClose]);

  // Determine mode
  const isCommandMode = query.startsWith('>');
  const commandQuery = isCommandMode ? query.slice(1).trim().toLowerCase() : '';

  const filteredActions = useMemo(() => {
    if (!isCommandMode) return [];
    if (!commandQuery) return commandActions.slice(0, 10);
    return commandActions
      .filter((action) =>
        action.keywords.some((kw) => kw.includes(commandQuery)) ||
        action.label.toLowerCase().includes(commandQuery)
      )
      .slice(0, 10);
  }, [isCommandMode, commandQuery, commandActions]);

  // Total selectable items for keyboard nav
  const totalItems = isCommandMode
    ? filteredActions.length
    : (query.length >= 2 ? results.length : (navigation?.recentItems?.length ?? 0));

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, totalItems - 1));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      }
      if (e.key === 'Enter' && isOpen) {
        e.preventDefault();
        if (isCommandMode && filteredActions[selectedIndex]) {
          filteredActions[selectedIndex].action();
        } else if (!isCommandMode && query.length >= 2 && results[selectedIndex]) {
          const result = results[selectedIndex];
          onSelectResult(result.category, result.id);
          if (navigation) {
            navigation.addToHistory(result.category, result.id, result.title);
          }
          onClose();
        } else if (!isCommandMode && query.length < 2 && navigation?.recentItems?.[selectedIndex]) {
          const item = navigation.recentItems[selectedIndex];
          onSelectResult(item.category, item.id);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, isCommandMode, filteredActions, selectedIndex, totalItems, query, results, navigation, onSelectResult]);

  return (
    <AnimatePresence>
    {isOpen && (
    <>
      <motion.div
        className="card-modal-backdrop"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
      />
      <motion.div
        ref={modalRef}
        className="search-modal"
        initial={{ opacity: 0, y: -20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.97 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
      >
        <div
          className="flex items-center gap-3 p-4"
          style={{ borderBottom: '1px solid var(--border-default)' }}
        >
          <Search style={{ color: 'var(--text-muted)' }} className="flex-shrink-0" size={20} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search or type > for commands..."
            className="search-input"
            autoFocus
          />
          <button
            type="button"
            onClick={onClose}
            className="icon-button flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        <div className="search-results">
          {isCommandMode ? (
            // Command palette mode
            filteredActions.length === 0 ? (
              <div className="p-8 text-center" style={{ color: 'var(--text-muted)' }}>
                No matching commands
              </div>
            ) : (
              <ul>
                {filteredActions.map((action, index) => {
                  const Icon = action.icon;
                  return (
                    <li key={action.id}>
                      <button
                        type="button"
                        onClick={action.action}
                        className="search-result-item"
                        style={{
                          background: index === selectedIndex ? 'var(--accent-muted)' : undefined,
                        }}
                      >
                        <Icon
                          className="mt-0.5 flex-shrink-0"
                          size={18}
                          style={{ color: 'var(--accent)' }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="search-result-title">
                            <span>{action.label}</span>
                          </div>
                          <div className="search-result-subtitle">{action.description}</div>
                        </div>
                        <ArrowRight size={14} style={{ color: 'var(--text-muted)' }} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )
          ) : query.length < 2 ? (
            <div className="p-2">
              {navigation?.recentItems && navigation.recentItems.length > 0 && (
                <div>
                  <div
                    className="px-4 py-2 text-xs font-semibold uppercase tracking-wider flex items-center gap-2"
                    style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}
                  >
                    <History size={12} /> Recent
                  </div>
                  <ul>
                    {navigation.recentItems.map((item, index) => {
                       const Icon = categoryIcons[item.category];
                       const colorClass = categoryColors[item.category];
                       return (
                         <li key={`history-${item.category}-${item.id}`}>
                           <button
                             onClick={() => onSelectResult(item.category, item.id)}
                             className="search-result-item w-full flex items-center gap-3"
                             style={{
                               background: index === selectedIndex ? 'var(--accent-muted)' : undefined,
                             }}
                           >
                             <div className={`p-1.5 rounded-md ${colorClass}`} style={{ background: 'var(--accent-muted)' }}>
                               <Icon size={16} />
                             </div>
                             <div className="flex-1 text-left">
                               <div className="font-medium" style={{ color: 'var(--text-primary)' }}>{item.name}</div>
                               <div className="text-xs capitalize" style={{ color: 'var(--text-muted)' }}>
                                 {item.category.replace(/([A-Z])/g, ' $1').trim()}
                               </div>
                             </div>
                           </button>
                         </li>
                       );
                    })}
                  </ul>
                </div>
              )}
              {(!navigation?.recentItems || navigation.recentItems.length === 0) && (
                <div className="p-8 text-center" style={{ color: 'var(--text-muted)' }}>
                  Type to search or <kbd style={{
                    padding: '0.125rem 0.375rem',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.7rem',
                  }}>&gt;</kbd> for commands
                </div>
              )}
            </div>
          ) : results.length === 0 ? (
            <div className="p-8 text-center" style={{ color: 'var(--text-muted)' }}>
              No results found for "{query}"
            </div>
          ) : (
            <ul>
              {results.map((result, index) => {
                const Icon = categoryIcons[result.category];
                const colorClass = categoryColors[result.category];
                return (
                  <li key={`${result.category}-${result.id}-${index}`}>
                    <button
                      type="button"
                      onClick={() => {
                        onSelectResult(result.category, result.id);
                        if (navigation) {
                          navigation.addToHistory(result.category, result.id, result.title);
                        }
                        onClose();
                      }}
                      className="search-result-item"
                      style={{
                        background: index === selectedIndex ? 'var(--accent-muted)' : undefined,
                      }}
                    >
                      <Icon className={`mt-0.5 ${colorClass}`} size={18} />
                      <div className="flex-1 min-w-0">
                        <div className="search-result-title">
                          <span>{result.title}</span>
                          <span className="meta-chip">{result.categoryLabel}</span>
                        </div>
                        {result.subtitle && (
                          <div className="search-result-subtitle">{result.subtitle}</div>
                        )}
                        <div className="search-result-match">
                          <span style={{ color: 'var(--text-muted)' }}>{result.matchedField}:</span>{' '}
                          {result.matchedText}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="search-footer">
          <span>
            <kbd>↑↓</kbd> navigate <kbd>↵</kbd> select
          </span>
          <span>
            <kbd>&gt;</kbd> commands <kbd>esc</kbd> close
          </span>
        </div>
      </motion.div>
    </>
    )}
    </AnimatePresence>
  );
}
